import AppIconButton from "../../components/form/AppIconButton";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import DashboardLayout from "../../widgets/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../widgets/Navbars/DashboardNavbar";
import { AppAutocomplete, AppButton, AppCheckbox, AppTextField } from "../../components/form";
import {
  AppDialog,
  AppDialogActions,
  AppDialogCancelButton,
  AppDialogContent,
  AppDialogDescription,
  AppDialogPrimaryButton,
  AppDialogTitle,
} from "../../components/dialog";
import {
  AppFormField,
  AppFormGrid,
  FormChoiceGroup,
  PageContentShell,
  PageSectionCard,
  ResponsiveActions,
} from "../../components/layout";
import { PageHeader, PageListContainer } from "../../components/Page";
import PackageLimitHint from "../../components/PackageLimitHint";
import LlmProviderAutocomplete from "../../components/LlmProviderAutocomplete";
import { GRID_FULL, GRID_HALF } from "../../lib/ui/formLayout";
import {
  workspaceService,
  type BeasLlmModelOption,
  type LlmMode,
  type WorkspaceMember,
  type WorkspaceRole,
  type WorkspaceSettings,
  type WorkspaceSummary,
} from "../../services/workspaceService";
import { useDeleteConfirm } from "../../hooks/useDeleteConfirm";
import {
  formatBytesMb,
  formatLimitValue,
  usePackageLimits,
} from "../../hooks/usePackageLimits";
import { useDeploymentMode } from "../../hooks/useDeploymentMode";
import { getCurrentUserRoles, isAdmin } from "../../utils/authorization";
import { clearSetupDeferred } from "../../utils/setupGate";
import EmbedsPanel from "../super-admin/ui/EmbedsPanel";
import WorkspaceSupportCard from "./WorkspaceSupportCard";
import DataIsolationCard from "./DataIsolationCard";

function StatusChip({ label, ok, okLabel, pendingLabel }: { label: string; ok: boolean; okLabel: string; pendingLabel: string }) {
  return (
    <Chip
      size="small"
      variant="outlined"
      color={ok ? "success" : "warning"}
      label={`${label}: ${ok ? okLabel : pendingLabel}`}
    />
  );
}

const INVITE_ROLES: WorkspaceRole[] = ["TENANT_ADMIN", "USER", "REPORTER", "VIEWER"];
type WorkspaceTab = "llm" | "opensearch" | "members" | "embeds" | "isolation" | "support";

export function WorkspaceSettingsContent() {
  const { t } = useTranslation();
  const { confirmDelete, isCancelled } = useDeleteConfirm();
  const packageLimits = usePackageLimits();
  const { onPrem } = useDeploymentMode();
  const [settings, setSettings] = useState<WorkspaceSettings | null>(null);
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<WorkspaceRole>("USER");
  const [llmApiKey, setLlmApiKey] = useState("");
  const [osPassword, setOsPassword] = useState("");
  const [llmMode, setLlmMode] = useState<LlmMode>("BEAS_MANAGED");
  const [beasLlmModels, setBeasLlmModels] = useState<BeasLlmModelOption[]>([]);
  const [beasLlmModelId, setBeasLlmModelId] = useState<string>("");
  const [osMode, setOsMode] = useState<"BEAS_MANAGED" | "EXTERNAL">("BEAS_MANAGED");
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [platformAdmin, setPlatformAdmin] = useState(false);
  const [workspaces, setWorkspaces] = useState<WorkspaceSummary[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [workspaceName, setWorkspaceName] = useState("");
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<WorkspaceTab>("llm");

  const reload = (workspaceId?: string | null) => {
    const id = workspaceId === undefined ? selectedId : workspaceId;
    if (platformAdmin && !id) {
      return workspaceService
        .listAll()
        .then((rows) => setWorkspaces(Array.isArray(rows) ? rows : []))
        .catch((e) => setError(e.message));
    }
    const settingsCall = id ? workspaceService.getSettingsById(id) : workspaceService.getSettings();
    const membersCall = id ? workspaceService.listMembers(id) : workspaceService.listMembers();
    return Promise.all([settingsCall, membersCall])
      .then(([s, m]) => {
        setSettings(s);
        setWorkspaceName(s.workspaceName || "");
        if (onPrem) {
          // On-prem: BEAS managed options are hidden; always show external connection forms.
          setOsMode("EXTERNAL");
          setLlmMode("EXTERNAL");
        } else if (s.openSearchMode === "EXTERNAL" || s.openSearchMode === "BEAS_MANAGED") {
          setOsMode(s.openSearchMode);
        }
        if (!onPrem) {
          if (s.llmMode === "EXTERNAL" || s.llmMode === "BEAS_MANAGED") {
            setLlmMode(s.llmMode);
          } else {
            setLlmMode(s.llmApiKeySet ? "EXTERNAL" : "BEAS_MANAGED");
          }
        }
        setBeasLlmModelId(s.beasLlmModelId || "");
        setMembers(Array.isArray(m) ? m : []);
      })
      .catch((e) => setError(e.message));
  };

  useEffect(() => {
    getCurrentUserRoles()
      .then((roles) => {
        const admin = isAdmin(roles);
        setPlatformAdmin(admin);
        if (admin) {
          return workspaceService.listAll().then((rows) => setWorkspaces(Array.isArray(rows) ? rows : []));
        }
        return reload(null);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!onPrem) return;
    setLlmMode("EXTERNAL");
    setOsMode("EXTERNAL");
  }, [onPrem]);

  useEffect(() => {
    if (loading || onPrem) return;
    // Platform admin has no tenant in JWT until a workspace is selected.
    if (platformAdmin && !selectedId) {
      setBeasLlmModels([]);
      return;
    }
    const call = selectedId
      ? workspaceService.listBeasLlmModelsById(selectedId)
      : workspaceService.listBeasLlmModels();
    call
      .then((rows) => setBeasLlmModels(Array.isArray(rows) ? rows : []))
      .catch(() => setBeasLlmModels([]));
  }, [loading, onPrem, platformAdmin, selectedId]);

  const persistBeasLlmSelection = async (): Promise<WorkspaceSettings> => {
    if (!beasLlmModelId) {
      throw new Error(t("workspace.setup.beasLlmPickRequired"));
    }
    return selectedId
      ? await workspaceService.selectBeasLlmById(selectedId, beasLlmModelId)
      : await workspaceService.selectBeasLlm(beasLlmModelId);
  };

  const save = async () => {
    if (!settings) return;
    setError(null);
    setSaving(true);
    const effectiveLlmMode: LlmMode = onPrem ? "EXTERNAL" : llmMode;
    const effectiveOsMode: "BEAS_MANAGED" | "EXTERNAL" = onPrem ? "EXTERNAL" : osMode;
    try {
      if (selectedId && workspaceName.trim() && workspaceName.trim() !== settings.workspaceName) {
        await workspaceService.updateWorkspace(selectedId, { name: workspaceName.trim() });
      }
      const llmExternalPayload = {
        llmMode: "EXTERNAL" as const,
        llmProvider: settings.llmProvider,
        llmBaseUrl: settings.llmBaseUrl,
        chatModel: settings.chatModel,
        embeddingModel: settings.embeddingModel,
        ...(llmApiKey ? { llmApiKey } : {}),
      };
      let s: WorkspaceSettings;
      if (effectiveLlmMode === "BEAS_MANAGED") {
        s = await persistBeasLlmSelection();
      } else {
        s = selectedId
          ? await workspaceService.saveSettingsById(selectedId, llmExternalPayload)
          : await workspaceService.saveSettings(llmExternalPayload);
      }

      const osPayload = {
        openSearchMode: effectiveOsMode,
        openSearchUrl: effectiveOsMode === "EXTERNAL" ? settings.openSearchUrl : undefined,
        openSearchUsername: effectiveOsMode === "EXTERNAL" ? settings.openSearchUsername : undefined,
        openSearchSslInsecure: settings.openSearchSslInsecure,
        indexCandidateCacheMinutes: Math.min(
          60,
          Math.max(1, Number(settings.indexCandidateCacheMinutes) || 5)
        ),
        ...(effectiveOsMode === "EXTERNAL" && osPassword ? { openSearchPassword: osPassword } : {}),
      };
      if (effectiveOsMode === "BEAS_MANAGED") {
        s = selectedId
          ? await workspaceService.provisionBeasOpenSearchById(selectedId)
          : await workspaceService.provisionBeasOpenSearch();
        const cachePayload = {
          indexCandidateCacheMinutes: osPayload.indexCandidateCacheMinutes,
        };
        s = selectedId
          ? await workspaceService.saveSettingsById(selectedId, cachePayload)
          : await workspaceService.saveSettings(cachePayload);
      } else {
        s = selectedId
          ? await workspaceService.saveSettingsById(selectedId, osPayload)
          : await workspaceService.saveSettings(osPayload);
      }
      setSettings(s);
      if (onPrem) {
        setOsMode("EXTERNAL");
        setLlmMode("EXTERNAL");
      } else {
        setOsMode(s.openSearchMode === "EXTERNAL" ? "EXTERNAL" : "BEAS_MANAGED");
        setLlmMode(s.llmMode === "EXTERNAL" ? "EXTERNAL" : "BEAS_MANAGED");
      }
      setBeasLlmModelId(s.beasLlmModelId || beasLlmModelId);
      setWorkspaceName(s.workspaceName || workspaceName);
      setLlmApiKey("");
      setOsPassword("");
      if (platformAdmin) {
        const rows = await workspaceService.listAll();
        setWorkspaces(Array.isArray(rows) ? rows : []);
      }
          setMessage(
        effectiveOsMode === "BEAS_MANAGED"
          ? t("workspace.setup.beasProvisioned")
          : t("workspace.setup.saved")
      );
    } catch (e: any) {
      setError(e.message);
      await reload(selectedId);
    } finally {
      setSaving(false);
    }
  };

  const testConnections = async () => {
    setError(null);
    setTesting(true);
    const effectiveLlmMode: LlmMode = onPrem ? "EXTERNAL" : llmMode;
    const effectiveOsMode: "BEAS_MANAGED" | "EXTERNAL" = onPrem ? "EXTERNAL" : osMode;
    try {
      // Persist pending BEAS LLM selection first so Test uses catalog credentials, not BYO fallbacks.
      if (effectiveLlmMode === "BEAS_MANAGED") {
        const s = await persistBeasLlmSelection();
        setSettings(s);
        setLlmMode(s.llmMode === "EXTERNAL" ? "EXTERNAL" : "BEAS_MANAGED");
        setBeasLlmModelId(s.beasLlmModelId || beasLlmModelId);
      }
      if (selectedId) {
        await workspaceService.testLlmById(selectedId);
        if (effectiveOsMode !== "BEAS_MANAGED") {
          await workspaceService.testOpenSearchById(selectedId);
        }
      } else {
        await workspaceService.testLlm();
        if (effectiveOsMode !== "BEAS_MANAGED") {
          await workspaceService.testOpenSearch();
        }
      }
      await reload(selectedId);
      if (platformAdmin) {
        const rows = await workspaceService.listAll();
        setWorkspaces(Array.isArray(rows) ? rows : []);
      }
      const status = await workspaceService.setupStatus().catch(() => null);
      if (status?.setupComplete) {
        clearSetupDeferred();
      }
      setMessage(t("workspace.setup.testsFinished"));
    } catch (e: any) {
      setError(e.message);
      await reload(selectedId);
    } finally {
      setTesting(false);
    }
  };

  const invite = async () => {
    setError(null);
    setInviting(true);
    try {
      await workspaceService.invite(inviteEmail.trim(), inviteRole, selectedId || undefined);
      setMessage(t("workspace.invite.sent"));
      setInviteEmail("");
      setInviteRole("USER");
      setInviteOpen(false);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setInviting(false);
    }
  };

  const removeMember = async (member: WorkspaceMember) => {
    try {
      await confirmDelete({ itemName: member.username || member.email });
      await workspaceService.removeMember(member.userId, selectedId || undefined);
      setMessage(t("workspace.members.removed"));
      await reload(selectedId);
    } catch (e: any) {
      if (isCancelled(e)) return;
      setError(e.message);
    }
  };

  const selectWorkspace = (id: string) => {
    setSelectedId(id);
    setError(null);
    setLoading(true);
    reload(id).finally(() => setLoading(false));
  };

  const selectedWorkspace = useMemo(
    () => workspaces.find((w) => w.id === selectedId) ?? null,
    [workspaces, selectedId]
  );
  const tabs = useMemo(() => {
    const items: { id: WorkspaceTab; label: string }[] = [
      { id: "llm", label: t("workspace.setup.llmModeTitle") },
      { id: "opensearch", label: t("workspace.setup.osModeTitle") },
      { id: "members", label: t("workspace.members.title") },
    ];
    if (!platformAdmin) {
      items.push({ id: "embeds", label: t("superAdmin.embeds.title") });
      items.push({ id: "isolation", label: t("dataIsolation.title") });
      if (!onPrem) items.push({ id: "support", label: t("workspaceSupport.title") });
    }
    return items;
  }, [platformAdmin, onPrem, t]);

  if (loading && !settings && !platformAdmin) {
    return (
      <PageListContainer>
        {error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Stack alignItems="center" py={4}>
            <CircularProgress size={28} />
          </Stack>
        )}
      </PageListContainer>
    );
  }

  const busy = saving || testing;
  const canMutate = Boolean(settings) && !(platformAdmin && !selectedId);
  const canInvite = canMutate;

  return (
    <PageListContainer>
      <PageHeader
        title={platformAdmin ? t("workspace.admin.title") : t("workspace.settingsTitle")}
        subtitle={settings?.workspaceName || t("workspace.admin.subtitle")}
        actions={
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap alignItems="center">
            {settings ? (
              <>
                <StatusChip
                  label={t("workspace.setup.steps.llm")}
                  ok={Boolean(settings.llmOk)}
                  okLabel={t("workspace.setup.statusOk")}
                  pendingLabel={t("common.notAvailable")}
                />
                <StatusChip
                  label={t("workspace.setup.steps.opensearch")}
                  ok={Boolean(settings.openSearchOk)}
                  okLabel={t("workspace.setup.statusOk")}
                  pendingLabel={t("common.notAvailable")}
                />
              </>
            ) : null}
            {settings && (tab === "llm" || tab === "opensearch") ? (
              <ResponsiveActions
                items={[
                  {
                    key: "test",
                    label: testing ? t("common.testing") : t("common.test"),
                    icon: "science",
                    onClick: () => void testConnections(),
                    disabled: busy || !canMutate,
                    variant: "outlined",
                  },
                  {
                    key: "save",
                    label: saving ? t("common.saving") : t("common.save"),
                    icon: "save",
                    onClick: () => void save(),
                    disabled: busy || !canMutate,
                    primary: true,
                  },
                ]}
              />
            ) : null}
          </Stack>
        }
      />

      {message && (
        <Alert severity="success" onClose={() => setMessage(null)} sx={{ mb: 1.5 }}>
          {message}
        </Alert>
      )}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 1.5 }}>
          {error}
        </Alert>
      )}
      {settings && !settings.setupComplete && !platformAdmin && (
        <Alert
          severity="warning"
          sx={{ mb: 1.5 }}
          action={
            <AppButton href="/setup" variant="outlined" color="inherit" size="small">
              {t("workspace.setup.openWizard")}
            </AppButton>
          }
        >
          {t("workspace.setup.incompleteBanner")}
        </Alert>
      )}

      {platformAdmin && (
        <Box sx={{ mb: 2 }}>
        <PageSectionCard
          title={t("workspace.admin.listTitle")}
          description={t("workspace.admin.listDescription")}
        >
          {workspaces.length === 0 ? (
            <Alert severity="info">{t("workspace.admin.empty")}</Alert>
          ) : (
            <AppAutocomplete<WorkspaceSummary>
              label={t("workspace.admin.name")}
              options={workspaces}
              value={selectedWorkspace}
              getOptionLabel={(w) =>
                `${w.name}${w.setupComplete ? "" : ` (${t("workspace.admin.incomplete")})`}`
              }
              isOptionEqualToValue={(a, b) => a.id === b.id}
              onChange={(_, value) => {
                if (value) selectWorkspace(value.id);
              }}
              helperText={
                selectedWorkspace?.ownerUsername
                  ? `${t("workspace.admin.owner")}: ${selectedWorkspace.ownerUsername}`
                  : undefined
              }
            />
          )}
        </PageSectionCard>
        </Box>
      )}

      {settings && (
        <Box sx={{ width: "100%" }}>
          <Tabs
            value={tab}
            onChange={(_, value: WorkspaceTab) => setTab(value)}
            variant="scrollable"
            allowScrollButtonsMobile
            sx={{
              mb: 2.5,
              minHeight: 40,
              borderBottom: 1,
              borderColor: "divider",
              "& .MuiTab-root": { minHeight: 40, textTransform: "none", fontWeight: 600 },
            }}
          >
            {tabs.map((item) => (
              <Tab key={item.id} value={item.id} label={item.label} />
            ))}
          </Tabs>

          {tab === "llm" && (
            <Stack spacing={2} sx={{ width: "100%" }}>
              {platformAdmin && (
                <PageSectionCard title={t("workspace.admin.editName")}>
                  <AppFormGrid>
                    <AppFormField size={GRID_FULL}>
                      <AppTextField
                        label={t("workspace.admin.name")}
                        value={workspaceName}
                        onChange={(e) => setWorkspaceName(e.target.value)}
                      />
                    </AppFormField>
                  </AppFormGrid>
                </PageSectionCard>
              )}

              <PageSectionCard
                title={t("workspace.setup.llmModeTitle")}
                description={t("workspace.setup.llmModeHint")}
              >
                <Stack spacing={1.5}>
                  <FormChoiceGroup
                    label={t("workspace.setup.llmMode")}
                    value={llmMode}
                    onChange={(v) => setLlmMode(v as LlmMode)}
                    options={[
                      ...(!onPrem
                        ? [{ value: "BEAS_MANAGED", label: t("workspace.setup.beasLlmTitle") }]
                        : []),
                      { value: "EXTERNAL", label: t("workspace.setup.ownLlmTitle") },
                    ]}
                  />
                  {llmMode === "BEAS_MANAGED" && !onPrem ? (
                    <AppFormGrid>
                      <AppFormField size={GRID_FULL}>
                        <AppAutocomplete
                          label={t("workspace.setup.beasLlmModel")}
                          options={beasLlmModels}
                          value={beasLlmModels.find((m) => m.id === beasLlmModelId) || null}
                          onChange={(_, v) => setBeasLlmModelId(v?.id || "")}
                          getOptionLabel={(o) => (o ? o.name : "")}
                          isOptionEqualToValue={(a, b) => a.id === b.id}
                          helperText={
                            settings.beasLlmModelName
                              ? t("workspace.setup.beasLlmSelected", { name: settings.beasLlmModelName })
                              : t("workspace.setup.beasLlmHelp")
                          }
                        />
                      </AppFormField>
                    </AppFormGrid>
                  ) : (
                    <AppFormGrid>
                      <AppFormField size={GRID_HALF}>
                        <LlmProviderAutocomplete
                          value={settings.llmProvider}
                          onChange={(llmProvider) => setSettings({ ...settings, llmProvider })}
                        />
                      </AppFormField>
                      <AppFormField size={GRID_HALF}>
                        <AppTextField
                          label={t("workspace.setup.llmBaseUrl")}
                          placeholder={
                            settings.llmProvider === "OLLAMA"
                              ? "http://127.0.0.1:11434"
                              : "https://api.openai.com"
                          }
                          value={settings.llmBaseUrl || ""}
                          onChange={(e) => setSettings({ ...settings, llmBaseUrl: e.target.value })}
                          helperText={t("workspace.setup.llmBaseUrlHelp")}
                        />
                      </AppFormField>
                      <AppFormField size={GRID_FULL}>
                        <AppTextField
                          label={
                            settings.llmApiKeySet
                              ? t("workspace.setup.llmApiKeyKeep")
                              : t("workspace.setup.llmApiKey")
                          }
                          type="password"
                          autoComplete="new-password"
                          value={llmApiKey}
                          onChange={(e) => setLlmApiKey(e.target.value)}
                        />
                      </AppFormField>
                      <AppFormField size={GRID_HALF}>
                        <AppTextField
                          label={t("workspace.setup.chatModel")}
                          value={settings.chatModel || ""}
                          onChange={(e) => setSettings({ ...settings, chatModel: e.target.value })}
                        />
                      </AppFormField>
                      <AppFormField size={GRID_HALF}>
                        <AppTextField
                          label={t("workspace.setup.embeddingModel")}
                          value={settings.embeddingModel || ""}
                          onChange={(e) => setSettings({ ...settings, embeddingModel: e.target.value })}
                        />
                      </AppFormField>
                    </AppFormGrid>
                  )}
                </Stack>
              </PageSectionCard>
            </Stack>
          )}

          {tab === "opensearch" && (
            <Stack spacing={2} sx={{ width: "100%" }}>
              <PageSectionCard
                title={t("workspace.setup.osModeTitle")}
                description={t("workspace.setup.osModeHint")}
              >
                <Stack spacing={1.5}>
                  <FormChoiceGroup
                    label={t("workspace.setup.osMode")}
                    value={osMode}
                    onChange={(v) => setOsMode(v as "BEAS_MANAGED" | "EXTERNAL")}
                    options={[
                      ...(!onPrem
                        ? [{ value: "BEAS_MANAGED", label: t("workspace.setup.beasTitle") }]
                        : []),
                      { value: "EXTERNAL", label: t("workspace.setup.externalTitle") },
                    ]}
                  />

                  {osMode === "BEAS_MANAGED" && !onPrem ? (
                    <Stack spacing={1}>
                      <Typography variant="body2" color="text.secondary">
                        {t("workspace.setup.beasNoFormHint")}
                      </Typography>
                      {packageLimits && (
                        <PackageLimitHint group="store" capped={packageLimits.beasOpenSearchQuotaBytes != null}>
                          {t("packageLimits.beasResource", {
                            mb: formatBytesMb(
                              packageLimits.beasOpenSearchQuotaBytes,
                              t("packageLimits.unlimited")
                            ),
                            indices:
                              packageLimits.beasOpenSearchMaxIndices != null
                                ? packageLimits.beasOpenSearchMaxIndices
                                : t("packageLimits.unlimited"),
                          })}
                        </PackageLimitHint>
                      )}
                      {settings.openSearchIndexPrefix && (
                        <Typography variant="caption" color="success.main" fontWeight={600}>
                          {t("workspace.setup.beasPrefixHint", {
                            prefix: settings.openSearchIndexPrefix,
                          })}
                        </Typography>
                      )}
                    </Stack>
                  ) : (
                    <AppFormGrid>
                      <AppFormField size={GRID_FULL}>
                        <AppTextField
                          label={t("workspace.setup.openSearchUrl")}
                          value={settings.openSearchUrl || ""}
                          onChange={(e) => setSettings({ ...settings, openSearchUrl: e.target.value })}
                        />
                      </AppFormField>
                      <AppFormField size={GRID_HALF}>
                        <AppTextField
                          label={t("workspace.setup.openSearchUsername")}
                          autoComplete="off"
                          value={settings.openSearchUsername || ""}
                          onChange={(e) =>
                            setSettings({ ...settings, openSearchUsername: e.target.value })
                          }
                        />
                      </AppFormField>
                      <AppFormField size={GRID_HALF}>
                        <AppTextField
                          label={
                            settings.openSearchPasswordSet
                              ? t("workspace.setup.openSearchPasswordKeep")
                              : t("workspace.setup.openSearchPassword")
                          }
                          type="password"
                          autoComplete="new-password"
                          value={osPassword}
                          onChange={(e) => setOsPassword(e.target.value)}
                        />
                      </AppFormField>
                      <AppFormField size={GRID_FULL}>
                        <AppCheckbox
                          label={t("workspace.setup.openSearchSslInsecure")}
                          checked={Boolean(settings.openSearchSslInsecure)}
                          onChange={(e) =>
                            setSettings({ ...settings, openSearchSslInsecure: e.target.checked })
                          }
                        />
                      </AppFormField>
                    </AppFormGrid>
                  )}
                  <AppFormGrid>
                    <AppFormField size={GRID_HALF}>
                      <AppTextField
                        label={t("workspace.setup.indexCandidateCacheMinutes")}
                        type="number"
                        inputProps={{ min: 1, max: 60, step: 1 }}
                        helperText={t("workspace.setup.indexCandidateCacheMinutesHelp")}
                        value={settings.indexCandidateCacheMinutes ?? 5}
                        onChange={(e) => {
                          const raw = Number(e.target.value);
                          const clamped = Number.isFinite(raw)
                            ? Math.min(60, Math.max(1, Math.round(raw)))
                            : 5;
                          setSettings({ ...settings, indexCandidateCacheMinutes: clamped });
                        }}
                      />
                    </AppFormField>
                  </AppFormGrid>
                </Stack>
              </PageSectionCard>
            </Stack>
          )}

          {tab === "members" && (
            <Box sx={{ width: "100%" }}>
            <PageSectionCard
              title={t("workspace.members.title")}
              description={t("workspace.members.description")}
              actions={
                <AppButton
                  size="small"
                  startIcon={<PersonAddAlt1OutlinedIcon />}
                  onClick={() => setInviteOpen(true)}
                  disabled={!canInvite}
                >
                  {t("workspace.invite.title")}
                </AppButton>
              }
              noPadding={members.length > 0}
            >
              <Stack spacing={1} sx={members.length > 0 ? { px: 1.5, pt: 1.25 } : undefined}>
                {packageLimits && (
                  <PackageLimitHint group="count" capped={packageLimits.maxWorkspaceUsers != null}>
                    {t("packageLimits.workspaceUsers", {
                      count: formatLimitValue(
                        packageLimits.maxWorkspaceUsers,
                        t("packageLimits.unlimited")
                      ),
                    })}
                  </PackageLimitHint>
                )}
                {platformAdmin && !selectedId && (
                  <Alert severity="info">{t("workspace.admin.selectFirst")}</Alert>
                )}
              </Stack>
              {members.length === 0 ? (
                <Alert severity="info" sx={{ mt: 1 }}>
                  {t("workspace.members.empty")}
                </Alert>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("workspace.members.user")}</TableCell>
                      <TableCell>{t("workspace.members.role")}</TableCell>
                      <TableCell align="right" width={56} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {members.map((member) => (
                      <TableRow key={member.userId}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {member.username}
                            {member.owner ? ` (${t("workspace.members.owner")})` : ""}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                            display="block"
                          >
                            {member.email || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            variant="outlined"
                            label={t(`workspace.roles.${member.role}`)}
                          />
                        </TableCell>
                        <TableCell align="right">
                          {!member.self && !member.owner && (
                            <AppIconButton
                              size="small"
                              title={t("common.delete")}
                              onClick={() => void removeMember(member)}
                            >
                              <DeleteOutlineOutlinedIcon fontSize="small" />
                            </AppIconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </PageSectionCard>
            </Box>
          )}

          {tab === "embeds" && !platformAdmin && (
            <Box sx={{ width: "100%" }}>
              <EmbedsPanel scope="workspace" />
            </Box>
          )}

          {tab === "isolation" && !platformAdmin && (
            <Box sx={{ width: "100%" }}>
              <DataIsolationCard />
            </Box>
          )}

          {tab === "support" && !platformAdmin && (
            <Box sx={{ width: "100%" }}>
              <WorkspaceSupportCard />
            </Box>
          )}
        </Box>
      )}

      <AppDialog
        open={inviteOpen}
        onClose={() => !inviting && setInviteOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <AppDialogTitle>{t("workspace.invite.title")}</AppDialogTitle>
        <AppDialogContent>
          <AppDialogDescription>{t("workspace.invite.description")}</AppDialogDescription>
          <AppTextField
            label={t("workspace.invite.email")}
            type="email"
            autoFocus
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && inviteEmail.trim()) {
                e.preventDefault();
                void invite();
              }
            }}
          />
          <AppAutocomplete
            label={t("workspace.invite.role")}
            options={INVITE_ROLES}
            value={inviteRole}
            disableClearable
            getOptionLabel={(role) => t(`workspace.roles.${role}`)}
            onChange={(_, value) => value && setInviteRole(value)}
          />
        </AppDialogContent>
        <AppDialogActions>
          <AppDialogCancelButton onClick={() => setInviteOpen(false)} disabled={inviting}>
            {t("common.cancel")}
          </AppDialogCancelButton>
          <AppDialogPrimaryButton
            onClick={() => void invite()}
            disabled={inviting || !inviteEmail.trim() || !canInvite}
            loading={inviting}
          >
            {inviting ? t("workspace.invite.sending") : t("workspace.invite.submit")}
          </AppDialogPrimaryButton>
        </AppDialogActions>
      </AppDialog>
    </PageListContainer>
  );
}

export default function WorkspaceSettingsPage() {
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <PageContentShell>
        <WorkspaceSettingsContent />
      </PageContentShell>
    </DashboardLayout>
  );
}
